<template>
  <div class="order-dashboard">
    <!-- 顶部标题 -->
    <div class="header-section">
      <h2 class="page-title">订单管理</h2>
      <p class="page-subtitle">查看用户订单记录，支持按状态筛选</p>
    </div>

    <div class="container">
      <!-- 搜索筛选栏 -->
      <div class="tableBar">
        <div class="search-area">
          <el-input
            v-model="searchKeyword"
            placeholder="订单号/商品名称/用户名"
            style="width: 280px"
            clearable
            @keyup.enter="handleSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-search" style="cursor: pointer" @click="handleSearch"></i>
          </el-input>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </div>
        
        <div class="filter-area">
          <el-select v-model="statusFilter" placeholder="订单状态" clearable style="width: 120px" @change="handleFilter">
            <el-option label="待支付" :value="0"></el-option>
            <el-option label="已支付" :value="1"></el-option>
            <el-option label="已取消" :value="2"></el-option>
            <el-option label="已完成" :value="3"></el-option>
          </el-select>
          
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            @change="handleFilter"
          ></el-date-picker>
        </div>
      </div>

      <!-- 订单表格 -->
      <el-table
        :data="tableData"
        v-loading="loading"
        border
        stripe
        style="width: 100%"
        class="data-table"
      >
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        
        <el-table-column prop="orderNo" label="订单号" width="200" align="center"></el-table-column>
        
        <el-table-column label="商品信息" min-width="250">
          <template #default="scope">
            <div class="product-info">
              <el-image
                style="width: 50px; height: 50px; border-radius: 4px; margin-right: 10px;"
                :src="scope.row.productImage"
                fit="cover"
              >
                <div slot="error" class="image-error">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              <div class="product-detail">
                <div class="product-title">{{ scope.row.productTitle }}</div>
                <div class="product-spec">规格：{{ scope.row.spec || '默认' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="价格信息" width="150" align="center">
          <template #default="scope">
            <div>单价：¥{{ scope.row.price }}</div>
            <div>数量：{{ scope.row.quantity }}</div>
            <div class="total-price">总价：¥{{ scope.row.totalPrice }}</div>
          </template>
        </el-table-column>
        
        <el-table-column prop="userName" label="用户信息" width="150" align="center">
          <template #default="scope">
            <div>{{ scope.row.userName }}</div>
            <div class="user-phone">{{ scope.row.userPhone }}</div>
          </template>
        </el-table-column>
        
        <el-table-column prop="paymentMethod" label="支付方式" width="100" align="center">
          <template #default="scope">
            {{ scope.row.paymentMethod === 'quick' ? '极速付' : '月付' }}
            <div v-if="scope.row.installment > 0" class="installment-tip">{{ scope.row.installment }}期分期</div>
          </template>
        </el-table-column>
        
        <el-table-column prop="couponCode" label="核销码" width="140" align="center">
          <template #default="scope">
            <el-tag type="info" size="small">{{ scope.row.couponCode }}</el-tag>
            <el-button type="text" size="small" @click="copyCode(scope.row.couponCode)">复制</el-button>
          </template>
        </el-table-column>
        
        <el-table-column label="订单状态" width="100" align="center">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="medium">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="支付时间" width="160" align="center">
          <template #default="scope">
            {{ formatDate(scope.row.payTime) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template #default="scope">
            <el-button type="primary" size="small" icon="el-icon-view" @click="viewDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </div>

    <!-- 订单详情弹窗 -->
    <el-dialog title="订单详情" v-model="detailVisible" width="700px">
      <div v-if="currentOrder" class="order-detail-content">
        <el-row :gutter="20">
          <el-col :span="24">
            <div class="detail-section">
              <h4 class="section-title">订单信息</h4>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">订单号：</span>
                  <span class="value">{{ currentOrder.orderNo }}</span>
                </div>
                <div class="info-item">
                  <span class="label">订单状态：</span>
                  <span class="value">
                    <el-tag :type="getStatusType(currentOrder.status)" size="small">
                      {{ getStatusText(currentOrder.status) }}
                    </el-tag>
                  </span>
                </div>
                <div class="info-item">
                  <span class="label">支付方式：</span>
                  <span class="value">{{ currentOrder.paymentMethod === 'quick' ? '极速付' : '月付' }}</span>
                </div>
                <div class="info-item" v-if="currentOrder.installment > 0">
                  <span class="label">分期数：</span>
                  <span class="value">{{ currentOrder.installment }}期</span>
                </div>
                <div class="info-item">
                  <span class="label">支付时间：</span>
                  <span class="value">{{ formatDate(currentOrder.payTime) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">创建时间：</span>
                  <span class="value">{{ formatDate(currentOrder.createTime) }}</span>
                </div>
              </div>
            </div>
          </el-col>
          
          <el-col :span="24">
            <div class="detail-section">
              <h4 class="section-title">商品信息</h4>
              <div class="product-detail-box">
                <el-image
                  style="width: 80px; height: 80px; border-radius: 4px; margin-right: 15px;"
                  :src="currentOrder.productImage"
                  fit="cover"
                ></el-image>
                <div class="product-detail-info">
                  <div class="product-title-large">{{ currentOrder.productTitle }}</div>
                  <div class="product-meta">
                    <span>规格：{{ currentOrder.spec || '默认' }}</span>
                    <span>数量：{{ currentOrder.quantity }}</span>
                    <span>单价：¥{{ currentOrder.price }}</span>
                  </div>
                  <div class="product-total">总价：<span class="total-highlight">¥{{ currentOrder.totalPrice }}</span></div>
                </div>
              </div>
            </div>
          </el-col>
          
          <el-col :span="24">
            <div class="detail-section">
              <h4 class="section-title">用户信息</h4>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">用户ID：</span>
                  <span class="value">{{ currentOrder.userId }}</span>
                </div>
                <div class="info-item">
                  <span class="label">用户姓名：</span>
                  <span class="value">{{ currentOrder.userName }}</span>
                </div>
                <div class="info-item">
                  <span class="label">联系电话：</span>
                  <span class="value">{{ currentOrder.userPhone }}</span>
                </div>
                <div class="info-item">
                  <span class="label">收货地址：</span>
                  <span class="value">{{ currentOrder.address }}</span>
                </div>
              </div>
            </div>
          </el-col>
          
          <el-col :span="24">
            <div class="detail-section">
              <h4 class="section-title">核销信息</h4>
              <div class="coupon-box">
                <div class="coupon-code">
                  券码：<strong>{{ currentOrder.couponCode }}</strong>
                  <el-button type="text" size="small" @click="copyCode(currentOrder.couponCode)">复制</el-button>
                </div>
                <div class="qrcode-preview">
                  <img :src="'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=' + currentOrder.couponCode" alt="二维码">
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
      
      <span slot="footer">
        <el-button type="primary" @click="detailVisible = false">关 闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';


const BASE_URL = 'http://localhost:8090/user';

export default {
  name: 'OrderManagement',
  data() {
    return {
      tableData: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      searchKeyword: '',
      statusFilter: null,
      dateRange: [],
      
      detailVisible: false,
      currentOrder: null
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    // 初始化加载数据
    init() {
      this.loading = true;
      
      // 获取所有订单（实际应该用分页接口，这里用getUserOrders模拟）
      axios.get(`${BASE_URL}/getUserOrders`)
        .then(res => {
          this.loading = false;
          if (res.data && res.data.code === 200) {
            let list = res.data.data || [];
            
            // 应用筛选
            list = this.applyFilters(list);
            
            this.total = list.length;
            // 手动分页
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            this.tableData = list.slice(start, end);
          } else {
            this.$message.error(res.data.msg || '加载失败');
          }
        })
        .catch(err => {
          this.loading = false;
          console.error('加载订单失败', err);
          this.$message.error('网络异常，请稍后重试');
        });
    },
    
    // 应用筛选条件
    applyFilters(list) {
      // 搜索关键词过滤
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase();
        list = list.filter(item => 
          (item.orderNo && item.orderNo.toLowerCase().includes(keyword)) ||
          (item.productTitle && item.productTitle.toLowerCase().includes(keyword)) ||
          (item.userName && item.userName.toLowerCase().includes(keyword))
        );
      }
      
      // 状态过滤
      if (this.statusFilter !== null && this.statusFilter !== '') {
        list = list.filter(item => item.status === this.statusFilter);
      }
      
      // 日期范围过滤
      if (this.dateRange && this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]).getTime();
        const endDate = new Date(this.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
        
        list = list.filter(item => {
          const payTime = item.payTime ? new Date(item.payTime).getTime() : 0;
          return payTime >= startDate && payTime <= endDate;
        });
      }
      
      return list;
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.init();
    },
    
    // 重置
    resetSearch() {
      this.searchKeyword = '';
      this.statusFilter = null;
      this.dateRange = [];
      this.handleSearch();
    },
    
    // 筛选变化
    handleFilter() {
      this.currentPage = 1;
      this.init();
    },
    
    // 查看详情
    viewDetail(row) {
      this.currentOrder = row;
      this.detailVisible = true;
    },
    
    // 复制券码
    copyCode(code) {
      this.$copyText(code).then(() => {
        this.$message.success('复制成功');
      }, () => {
        this.$message.error('复制失败');
      });
    },
    
    // 获取状态类型
    getStatusType(status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'info',
        3: 'primary'
      };
      return map[status] || 'info';
    },
    
    // 获取状态文本
    getStatusText(status) {
      const map = {
        0: '待支付',
        1: '已支付',
        2: '已取消',
        3: '已完成'
      };
      return map[status] || '未知';
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        const pad = value => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      } catch {
        return dateStr;
      }
    },
    
    // 分页
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.init();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.init();
    }
  }
};
</script>

<style lang="less" scoped>
.order-dashboard {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.header-section {
  text-align: center;
  margin-bottom: 20px;
  .page-title { font-size: 24px; color: #303133; margin-bottom: 5px; }
  .page-subtitle { color: #909399; font-size: 14px; }
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

.tableBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
  
  .filter-area {
    display: flex;
    gap: 10px;
  }
}

.data-table {
  margin-bottom: 20px;
}

.product-info {
  display: flex;
  align-items: center;
  
  .product-detail {
    flex: 1;
    .product-title {
      font-weight: bold;
      margin-bottom: 3px;
    }
    .product-spec {
      font-size: 12px;
      color: #909399;
    }
  }
}

.total-price {
  color: #F56C6C;
  font-weight: bold;
  margin-top: 3px;
}

.user-phone {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
}

.installment-tip {
  font-size: 11px;
  color: #E6A23C;
  margin-top: 2px;
}

.image-error {
  width: 50px;
  height: 50px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 16px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.order-detail-content {
  .detail-section {
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 1px solid #EBEEF5;
    
    &:last-child {
      border-bottom: none;
    }
    
    .section-title {
      margin: 0 0 15px 0;
      color: #303133;
      font-size: 16px;
      font-weight: bold;
    }
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    
    .info-item {
      .label {
        color: #909399;
        font-size: 13px;
      }
      .value {
        color: #303133;
        font-weight: 500;
        margin-left: 5px;
      }
    }
  }
  
  .product-detail-box {
    display: flex;
    align-items: flex-start;
    
    .product-detail-info {
      flex: 1;
      
      .product-title-large {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 8px;
      }
      
      .product-meta {
        margin-bottom: 8px;
        span {
          margin-right: 15px;
          color: #606266;
          font-size: 13px;
        }
      }
      
      .product-total {
        .total-highlight {
          color: #F56C6C;
          font-size: 18px;
          font-weight: bold;
        }
      }
    }
  }
  
  .coupon-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f9f9f9;
    padding: 15px;
    border-radius: 8px;
    
    .coupon-code {
      font-size: 16px;
      strong {
        font-size: 20px;
        color: #E6A23C;
        letter-spacing: 1px;
      }
    }
    
    .qrcode-preview {
      img {
        width: 100px;
        height: 100px;
        border-radius: 4px;
      }
    }
  }
}
</style>
